import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { mergePublicTheme, publicThemeVars } from '../publicTheme';
import { usePublicSocket } from '../hooks/usePublicSocket';
import PublicNavbar from './PublicNavbar';
import TicketMedia from './TicketMedia';

const STATUS_COLOR = {
  ABIERTO: '#60a5fa',
  'EN CURSO': '#f59e0b',
  BLOQUEADO: '#ef4444',
  FINALIZADO: '#22c55e',
};

const center = { maxWidth: '480px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--pub-muted, #94a3b8)' };
const primaryBtn = { padding: '11px 22px', background: 'var(--pub-accent, #38bdf8)', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' };
const secondaryBtn = { padding: '7px 16px', background: 'none', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', color: 'var(--pub-text, #ffffff)', borderRadius: '7px', cursor: 'pointer' };
const inputStyle = { width: '100%', padding: '10px 12px', background: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '8px', color: 'var(--pub-text, #ffffff)', fontSize: '0.92rem', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const labelStyle = { display: 'block', color: 'var(--pub-muted, #94a3b8)', fontSize: '0.78rem', marginBottom: '5px', fontWeight: 500 };

function PublicTicket() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState('menu'); // 'menu' | 'create' | 'consult'

  // Crear
  const [form, setForm] = useState({ title: '', description: '', name: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [createError, setCreateError] = useState('');
  const [createFiles, setCreateFiles] = useState([]); // [{ url, type, name }]
  const [uploadingCreate, setUploadingCreate] = useState(false);
  const createFileRef = useRef(null);

  // Consultar (por número de celular)
  const [queryPhone, setQueryPhone] = useState('');
  const [ticketList, setTicketList] = useState(null); // null = aún no buscó; [] = sin resultados
  const [ticket, setTicket] = useState(null);
  const [consultError, setConsultError] = useState('');
  const [loadingConsult, setLoadingConsult] = useState(false);
  const [clientComment, setClientComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [commentAttachment, setCommentAttachment] = useState(null); // { url, type, name }
  const [uploadingComment, setUploadingComment] = useState(false);
  const commentFileRef = useRef(null);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    fetch(`/api/ticket/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setStore)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (commentsEndRef.current) commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.comments?.length]);

  // Tiempo real: cuando el admin responde, cambia el estado o hace visible un
  // comentario, el servidor emite 'ticket_changed' al portal público. Refrescamos
  // el ticket que el cliente tiene abierto sin recargar ni mostrar "Cargando".
  const ticketRef = useRef(null);
  ticketRef.current = ticket;
  usePublicSocket(slug, ['ticket_changed'], () => {
    const open = ticketRef.current;
    if (!open?.id) return;
    fetch(`/api/ticket/${encodeURIComponent(slug)}/${encodeURIComponent(open.id)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j) setTicket(j); })
      .catch(() => {});
  });

  // Sube un archivo al portal y devuelve { url, type, name } (o null si falla).
  const uploadTicketFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`/api/ticket/${encodeURIComponent(slug)}/upload`, { method: 'POST', body: fd });
    const json = await r.json().catch(() => ({}));
    if (r.ok && json.url) return { url: json.url, type: json.type, name: json.name };
    return null;
  };

  const addCreateFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingCreate(true);
    setCreateError('');
    for (const file of files) {
      const att = await uploadTicketFile(file).catch(() => null);
      if (att) setCreateFiles(prev => [...prev, att]);
      else setCreateError('No se pudo subir un archivo');
    }
    setUploadingCreate(false);
    if (createFileRef.current) createFileRef.current.value = '';
  };

  const submitCreate = async () => {
    if (!form.title.trim()) { setCreateError('El título es obligatorio'); return; }
    setSubmitting(true);
    setCreateError('');
    try {
      const r = await fetch(`/api/ticket/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, attachments: createFiles }),
      });
      const json = await r.json().catch(() => ({}));
      if (r.ok) {
        setCreatedId(json.id);
        setForm({ title: '', description: '', name: '', phone: '' });
        setCreateFiles([]);
      } else {
        setCreateError(json.message || 'Error al crear el ticket');
      }
    } catch {
      setCreateError('Error de conexión');
    }
    setSubmitting(false);
  };

  // Busca todos los tickets asociados a un número de celular y los lista del más
  // reciente al más antiguo para que el cliente escoja cuál abrir.
  const lookupByPhone = async (phoneArg) => {
    const phone = (phoneArg ?? queryPhone).toString().trim();
    if (!phone) return;
    setLoadingConsult(true);
    setConsultError('');
    setTicket(null);
    setTicketList(null);
    try {
      const r = await fetch(`/api/ticket/${encodeURIComponent(slug)}/by-phone?phone=${encodeURIComponent(phone)}`);
      const json = await r.json().catch(() => ({}));
      if (r.ok) {
        setTicketList(Array.isArray(json.tickets) ? json.tickets : []);
      } else {
        setConsultError(json.message || 'No pudimos consultar tus tickets');
      }
    } catch {
      setConsultError('Error de conexión');
    }
    setLoadingConsult(false);
  };

  // Abre un ticket puntual por id (tras escogerlo de la lista, tras crearlo, o al
  // refrescar en tiempo real).
  const consult = async (id) => {
    const tid = (id ?? '').toString().trim();
    if (!tid) return;
    setLoadingConsult(true);
    setConsultError('');
    setTicket(null);
    try {
      const r = await fetch(`/api/ticket/${encodeURIComponent(slug)}/${encodeURIComponent(tid)}`);
      const json = await r.json().catch(() => ({}));
      if (r.ok) setTicket(json);
      else setConsultError(json.message || 'No encontramos el ticket');
    } catch {
      setConsultError('Error de conexión');
    }
    setLoadingConsult(false);
  };

  const uploadCommentFile = async (file) => {
    if (!file) return;
    setUploadingComment(true);
    setConsultError('');
    const att = await uploadTicketFile(file).catch(() => null);
    if (att) setCommentAttachment(att);
    else setConsultError('No se pudo subir el archivo');
    setUploadingComment(false);
    if (commentFileRef.current) commentFileRef.current.value = '';
  };

  const addClientComment = async () => {
    const text = clientComment.trim();
    if (!text && !commentAttachment) return;
    setSendingComment(true);
    try {
      const body = { text };
      if (commentAttachment) { body.media_url = commentAttachment.url; body.media_type = commentAttachment.type; body.media_name = commentAttachment.name; }
      const r = await fetch(`/api/ticket/${encodeURIComponent(slug)}/${ticket.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setClientComment('');
        setCommentAttachment(null);
        await consult(ticket.id);
      } else {
        const json = await r.json().catch(() => ({}));
        setConsultError(json.message || 'No se pudo agregar el comentario');
      }
    } catch {
      setConsultError('Error de conexión');
    }
    setSendingComment(false);
  };

  const theme = mergePublicTheme(store?.theme);

  if (notFound) return <main style={center}>Portal de tickets no disponible.</main>;
  if (!store) return <main style={center}>Cargando...</main>;

  const isFinalized = ticket?.status === 'FINALIZADO';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background, color: theme.colors.text, ...publicThemeVars(theme.colors) }}>
    <PublicNavbar slug={slug} store={store} current="ticket" subtitle="Soporte y tickets (PQR)" maxWidth="1000px" />
    <main style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1.5rem 1.5rem' }}>
      {/* ─── MENÚ PRINCIPAL ─── */}
      {view === 'menu' && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '2rem' }}>
          <button onClick={() => { setView('create'); setCreatedId(null); setCreateError(''); }} style={{ ...primaryBtn, flex: '1 1 260px', maxWidth: '340px', padding: '1.4rem' }}>
            ＋ Crear ticket de soporte
          </button>
          <button onClick={() => { setView('consult'); setTicket(null); setTicketList(null); setQueryPhone(''); setConsultError(''); }} style={{ ...secondaryBtn, flex: '1 1 260px', maxWidth: '340px', padding: '1.4rem', fontWeight: 600, fontSize: '0.95rem' }}>
            🔍 Consultar ticket de soporte
          </button>
        </div>
      )}

      {/* ─── CREAR ─── */}
      {view === 'create' && (
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <button onClick={() => setView('menu')} style={{ ...secondaryBtn, marginBottom: '1.2rem' }}>← Volver</button>
          {createdId ? (
            <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.6rem', marginBottom: '0.5rem' }}>✅</div>
              <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem', fontSize: '1.2rem' }}>¡Ticket creado!</h2>
              <p style={{ color: theme.colors.text, marginBottom: '0.5rem' }}>El id de tu ticket es:</p>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: theme.colors.primary, fontFamily: 'monospace', letterSpacing: '1px', margin: '0.5rem 0 1rem' }}>#{createdId}</div>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', lineHeight: 1.6 }}>
                Guarda este id de ticket para venir a consultar cómo va tu ticket de soporte.
              </p>
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => { setView('consult'); setTicketList(null); consult(createdId); }} style={primaryBtn}>Consultar mi ticket</button>
                <button onClick={() => setCreatedId(null)} style={secondaryBtn}>Crear otro</button>
              </div>
            </div>
          ) : (
            <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '1.6rem' }}>
              <h2 style={{ color: theme.colors.primary, margin: '0 0 1.2rem', fontSize: '1.15rem' }}>Nuevo ticket de soporte</h2>
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Título / Asunto *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={250} style={inputStyle} placeholder="Resumen del problema" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Descripción</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={5} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Cuéntanos con detalle qué sucede" />
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', marginBottom: '1rem' }}>
                  <label style={labelStyle}>Tu nombre</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={255} style={inputStyle} placeholder="Nombre" />
                </div>
                <div style={{ flex: '1 1 200px', marginBottom: '1rem' }}>
                  <label style={labelStyle}>Tu teléfono</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} maxLength={30} style={inputStyle} placeholder="Teléfono / WhatsApp" />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Adjuntar archivos (opcional)</label>
                <input ref={createFileRef} type="file" multiple onChange={e => addCreateFiles(e.target.files)} style={{ display: 'none' }} />
                <button type="button" onClick={() => createFileRef.current?.click()} disabled={uploadingCreate} style={{ ...secondaryBtn, width: '100%', cursor: uploadingCreate ? 'wait' : 'pointer' }}>
                  {uploadingCreate ? 'Subiendo...' : '📎 Adjuntar imágenes, videos o documentos'}
                </button>
                {createFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                    {createFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', fontSize: '0.82rem' }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.colors.text }}>📎 {f.name}</span>
                        <button type="button" onClick={() => setCreateFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '0.95rem' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {createError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '0.8rem' }}>{createError}</p>}
              <button onClick={submitCreate} disabled={submitting || uploadingCreate} style={{ ...primaryBtn, width: '100%' }}>
                {submitting ? 'Enviando...' : 'Enviar ticket'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── CONSULTAR ─── */}
      {view === 'consult' && (
        <div>
          <button onClick={() => { setView('menu'); setTicket(null); setTicketList(null); }} style={{ ...secondaryBtn, marginBottom: '1.2rem' }}>← Volver</button>
          {!ticket && (
            <div style={{ maxWidth: '460px', margin: '0 auto', background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '1.6rem' }}>
              <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem', fontSize: '1.15rem' }}>Consultar ticket</h2>
              <label style={labelStyle}>Ingresa el número de celular con el que se creó el ticket</label>
              <input value={queryPhone} onChange={e => setQueryPhone(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') lookupByPhone(); }} type="tel" style={inputStyle} placeholder="Ej: 300 123 4567" />
              {consultError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', margin: '0.8rem 0 0' }}>{consultError}</p>}
              <button onClick={() => lookupByPhone()} disabled={loadingConsult} style={{ ...primaryBtn, width: '100%', marginTop: '1rem' }}>
                {loadingConsult ? 'Buscando...' : 'Buscar mis tickets'}
              </button>

              {/* Resultados: lista de tickets del más reciente al más antiguo */}
              {ticketList && ticketList.length === 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.88rem', textAlign: 'center', margin: '1.2rem 0 0' }}>
                  No encontramos tickets asociados a ese número de celular.
                </p>
              )}
              {ticketList && ticketList.length > 0 && (
                <div style={{ marginTop: '1.4rem' }}>
                  <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>
                    {ticketList.length} {ticketList.length === 1 ? 'ticket encontrado' : 'tickets encontrados'} · elige cuál ver
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {ticketList.map(t => (
                      <button
                        key={t.id}
                        onClick={() => consult(t.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', textAlign: 'left', width: '100%', padding: '0.7rem 0.9rem', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '9px', cursor: 'pointer', color: theme.colors.text }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontSize: '0.92rem' }}>#{t.id} · {t.title}</div>
                          <div style={{ fontSize: '0.72rem', color: theme.colors.textMuted, marginTop: '2px' }}>
                            {t.created_at ? new Date(t.created_at).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                          </div>
                        </div>
                        <span style={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, padding: '3px 9px', borderRadius: '6px', backgroundColor: `${STATUS_COLOR[t.status]}22`, color: STATUS_COLOR[t.status] }}>{t.status}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {ticket && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <button onClick={() => { setTicket(null); setConsultError(''); }} style={secondaryBtn}>← {ticketList && ticketList.length > 0 ? 'Volver a mis tickets' : 'Consultar otro'}</button>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '4px 12px', borderRadius: '6px', backgroundColor: `${STATUS_COLOR[ticket.status]}22`, color: STATUS_COLOR[ticket.status] }}>{ticket.status}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Datos del ticket (solo lectura) */}
                <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '1.4rem' }}>
                  <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ticket #{ticket.id}</div>
                  <h2 style={{ color: theme.colors.text, margin: '4px 0 0.8rem', fontSize: '1.2rem' }}>{ticket.title}</h2>
                  <p style={{ color: theme.colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0 }}>{ticket.description || 'Sin descripción.'}</p>
                </div>

                {/* Panel de comentarios */}
                <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '0.8rem 1.2rem', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comentarios</div>
                  <div style={{ maxHeight: '380px', overflow: 'auto', padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '0.7rem', background: theme.colors.background }}>
                    {(!ticket.comments || ticket.comments.length === 0) && (
                      <p style={{ color: theme.colors.textMuted, fontSize: '0.88rem', textAlign: 'center', margin: '0.5rem 0' }}>Aún no hay comentarios.</p>
                    )}
                    {(ticket.comments || []).map((c, i) => (
                      <div key={i} style={{ alignSelf: c.from_client ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                        <div style={{
                          background: c.from_client ? 'rgba(59,130,246,0.15)' : theme.colors.surface,
                          border: `1px solid ${c.from_client ? 'rgba(59,130,246,0.4)' : theme.colors.border}`,
                          borderRadius: '10px', padding: '8px 11px',
                        }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: c.from_client ? '#60a5fa' : '#f59e0b', marginBottom: '2px' }}>{c.user_name}</div>
                          {c.text && <div style={{ color: theme.colors.text, fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</div>}
                          {c.media_url && <TicketMedia url={c.media_url} type={c.media_type} name={c.media_name} muted={theme.colors.textMuted} accent={theme.colors.primary} />}
                          <div style={{ fontSize: '0.65rem', color: theme.colors.textMuted, marginTop: '3px' }}>
                            {c.created_at ? new Date(c.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={commentsEndRef} />
                  </div>

                  {isFinalized ? (
                    <div style={{ padding: '1rem 1.2rem', borderTop: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, fontSize: '0.85rem', textAlign: 'center' }}>
                      Este ticket está finalizado. No se pueden agregar más comentarios.
                    </div>
                  ) : (
                    <div style={{ borderTop: `1px solid ${theme.colors.border}` }}>
                      {commentAttachment && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0.8rem 1.2rem 0', padding: '6px 10px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', fontSize: '0.82rem' }}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.colors.text }}>📎 {commentAttachment.name}</span>
                          <button onClick={() => setCommentAttachment(null)} style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '0.95rem' }}>✕</button>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', padding: '0.8rem 1.2rem', alignItems: 'flex-end' }}>
                        <input ref={commentFileRef} type="file" style={{ display: 'none' }} onChange={e => uploadCommentFile(e.target.files[0])} />
                        <button onClick={() => commentFileRef.current?.click()} disabled={uploadingComment} title="Adjuntar archivo" style={{ ...secondaryBtn, padding: '10px 12px', cursor: uploadingComment ? 'wait' : 'pointer' }}>{uploadingComment ? '⏳' : '📎'}</button>
                        <textarea
                          value={clientComment}
                          onChange={e => setClientComment(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addClientComment(); } }}
                          placeholder="Escribe un comentario..."
                          rows={1}
                          style={{ flex: 1, resize: 'none', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '10px 12px', color: theme.colors.text, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', maxHeight: '120px' }}
                        />
                        <button onClick={addClientComment} disabled={sendingComment || uploadingComment || (!clientComment.trim() && !commentAttachment)} style={{ ...primaryBtn, padding: '10px 18px' }}>Enviar</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
    </div>
  );
}

export default PublicTicket;
